/**
 * Template D, the drill's header — the designer's brief part 2 §5, `docs/adr/9032`. Reparto →
 * negozio → articolo, the sales history, the run's provenance, the consolidation gate, the plan
 * detail, a simulation's results. The body under it is template S.
 *
 *   the breadcrumb   12px, in a band over a `rule-soft` rule. The FIRST crumb is «App · Pagina»,
 *                    the page the drill started from; the ancestors are `crumb-link` links,
 *                    underlined with a 3px offset; the last is the entity, ink 600, not a link;
 *                    the separator «›» is `ink-muted`
 *   the header       the kicker (the entity's TYPE, 11px uppercase) and the title (its NAME, Antonio
 *                    28); at the inline end, the drill's constants band (market · run · parent)
 *
 * **One way up.** A drill is climbed from the breadcrumb only: this header draws no «torna a»
 * link and no arrows between siblings, and a page under it should not either.
 *
 * **A name not published is a code, never a blank.** When the platform publishes no name for the
 * entity (bata-platform#470) the title is its code in mono with the «Nome in arrivo» tag. A page
 * that knows better says so with `entity.nameNote`: the platform's reason in words where it gave one
 * (a door the store master does not hold is not «coming»), or nothing where it has not been asked yet.
 */

import { useTranslation } from 'react-i18next';

import { ConstantsBand, InAppAnchor, type ConstantPair } from './parts';

export interface DrillCrumb {
  /** The ancestor's name, resolved — «Donna · Sneaker», «Santafé». */
  readonly label: string;
  readonly href: string;
  /** A code rather than a name — set in mono. */
  readonly mono?: boolean;
}

export interface DrillHeaderProps {
  /** Where the drill started: the app and page, the first crumb — «Riassortimento · Allocazione». */
  readonly origin: { readonly app: string; readonly page: string; readonly href: string };
  /** The levels between the origin and this entity, top first. */
  readonly ancestors?: readonly DrillCrumb[];
  /**
   * This entity. `name` `null`: not published (bata-platform#470) — the code stands in, tagged
   * «Nome in arrivo» unless `nameNote` says otherwise: a sentence (why there is no name), or `null`
   * for no tag at all (the read that would name it has not answered).
   */
  readonly entity: { readonly name: string | null; readonly code: string; readonly nameNote?: string | null };
  /** The entity's type — «Negozio», «Articolo in negozio», «Piano di uscita». */
  readonly kickerKey: string;
  /** The drill's constants — market · run · parent. */
  readonly constants: readonly ConstantPair[];
  /** The title's level: `h1` where the drill header is the page's heading. */
  readonly headingLevel?: 1 | 2;
}

const LINK = 'text-crumb-link rounded-control underline underline-offset-[3px]';

export function DrillHeader({
  origin, ancestors = [], entity, kickerKey, constants, headingLevel = 2,
}: DrillHeaderProps): React.JSX.Element {
  const { t } = useTranslation();
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const unnamed = entity.name === null;
  return (
    <header data-template="drill" data-tour="drill-header" className="border-hairline bg-surface-raised flex flex-col border">
      <nav aria-label={t('template.drill.breadcrumb')} data-drill-breadcrumb="" className="border-rule-soft border-b px-6 py-[10px]">
        <ol className="flex flex-wrap items-center gap-[6px] text-[12px] leading-4">
          <li className="flex items-center gap-[6px]">
            <InAppAnchor href={origin.href} className={LINK}>
              {t('template.drill.origin', { app: origin.app, page: origin.page })}
            </InAppAnchor>
            <Separator />
          </li>
          {ancestors.map((crumb) => (
            <li key={crumb.href} className="flex items-center gap-[6px]">
              <InAppAnchor href={crumb.href} className={`${LINK} ${crumb.mono === true ? 'font-numeric' : ''}`}>
                {crumb.label}
              </InAppAnchor>
              <Separator />
            </li>
          ))}
          <li aria-current="page" className={`text-ink-primary font-semibold ${unnamed ? 'font-numeric' : ''}`}>
            {entity.name ?? entity.code}
          </li>
        </ol>
      </nav>

      {/*
        A wrapping row, not a grid with an `auto` column: an `auto` track is given its whole max-content
        BEFORE the `1fr` track takes what is left, so a constants band of five pairs (market, base run,
        created, scope, id) ate the row and left the title ~120px, the kicker one word to a line and the
        title running under the band. Here the title asks for a floor (``flex-[1_1_24rem]`, a basis it grows
        from) and the band goes beside it only when the row has room for both; otherwise it wraps UNDER
        the title at the full width. ADR 9070 R22.
      */}
      <div data-drill-head="" className="flex flex-wrap items-end gap-x-6 gap-y-3 px-6 pb-4 pt-[14px]">
        <div data-drill-titleblock="" className="flex min-w-0 flex-[1_1_24rem] flex-col gap-1">
          <p data-drill-kicker="" className="text-ink-secondary text-[11px] uppercase tracking-[0.1em]">{t(kickerKey)}</p>
          <Heading data-drill-title="" className="text-ink-primary flex flex-wrap items-center gap-3">
            {unnamed ? (
              <>
                <span className="font-numeric text-[28px] font-semibold leading-none">{entity.code}</span>
                {entity.nameNote === undefined
                  ? <NameArrivingTag />
                  : entity.nameNote === null
                    ? null
                    : <span data-name-absent="" className="text-ink-secondary text-caption">{entity.nameNote}</span>}
              </>
            ) : (
              <span className="font-display text-[28px] font-bold uppercase leading-none">{entity.name}</span>
            )}
          </Heading>
        </div>
        <div data-drill-constants="" className="min-w-0 max-w-full">
          <ConstantsBand pairs={constants} labelKey="template.drill.constants" />
        </div>
      </div>
    </header>
  );
}

function Separator(): React.JSX.Element {
  return <span aria-hidden className="text-ink-muted">›</span>;
}

/** «Nome in arrivo»: the «In arrivo» tag's look, for a name the platform does not publish yet. */
function NameArrivingTag(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span
      data-name-arriving=""
      className="border-ink-primary bg-surface-raised text-ink-primary inline-flex shrink-0 items-center border px-2 py-[2px] text-[11px] font-bold uppercase leading-[14px] tracking-[0.08em]"
    >
      {t('template.drill.nameArriving')}
    </span>
  );
}

/** The kickers of the brief's §5 table — the entity types a drill names. */
export const DRILL_KICKER_KEYS = {
  department: 'template.drill.kicker.department',
  store: 'template.drill.kicker.store',
  storeArticle: 'template.drill.kicker.storeArticle',
  consolidationGate: 'template.drill.kicker.consolidationGate',
  exitPlan: 'template.drill.kicker.exitPlan',
  simulationResults: 'template.drill.kicker.simulationResults',
} as const;

/** The constants' labels of the brief's §5 table. */
export const DRILL_CONSTANT_KEYS = {
  market: 'template.drill.constant.market',
  run: 'template.drill.constant.run',
  parent: 'template.drill.constant.parent',
  option: 'template.drill.constant.option',
  season: 'template.drill.constant.season',
  state: 'template.drill.constant.state',
  deadline: 'template.drill.constant.deadline',
  baseRun: 'template.drill.constant.baseRun',
  created: 'template.drill.constant.created',
} as const;
